import React, { useCallback, useMemo } from "react";
import { useApolloClient } from "@apollo/client";
import _get from "lodash/get";
import * as Preview from "@repoflow/components/preview";
import { useEditor } from "@repoflow/components/editor";
import { previewSrc } from "env";
import * as SiteQL from "queries/site";
import * as BlogPublishedQL from "queries/blog-published";
import * as BlogCollectionQL from "queries/blog-collection";
import { fetchBlogData } from "render/blog-data";

import { samplePage } from "./sample";

const SITE = "data.viewer.account.sites.get";

// What the sample page points at: the newest published post for a post
// template, the first collection for a collection template, nothing for the
// listing. Each read `.catch`-ed to null, so a site with nothing behind it
// previews the element's own "not found" rather than an empty pane.
const sampleOf = async (client, siteId, kind) => {
  if (kind === "post") {
    const res = await client
      .query({
        query: BlogPublishedQL.page,
        variables: { siteId, input: { page: 1, limit: 1, tag: "" } },
        fetchPolicy: "network-only",
      })
      .catch(() => null);
    const items = _get(res, `${SITE}.blog.published.list.items`) || [];
    return { newest: items[0] || null };
  }

  if (kind === "collection") {
    const res = await client
      .query({
        query: BlogCollectionQL.list,
        variables: { siteId, input: { limit: 1 } },
        fetchPolicy: "network-only",
      })
      .catch(() => null);
    const items = _get(res, `${SITE}.blog.collections.list.items`) || [];
    return { collection: items[0] || null };
  }

  return {};
};

// The right-hand pane of the template editor: the template as it renders,
// live, around a real post.
//
// The section editor's frame, adapted the same way it adapted the page
// editor's: one element rather than a page, nothing to select, and what goes
// in is what differs. Here what goes in is a page ./sample builds and the blog
// data the page editor's frame would load for it — with the template being
// typed put OVER the site's saved one in `data.templates`, which is the one
// place the composer reads a blog template from (templateFrom in
// @common/render/blog). So the pane shows the unsaved template, and shows it
// through exactly the code the served page will use.
export const Frame = ({ siteId, kind, state }) => {
  const client = useApolloClient();
  const { auto, setAuto } = useEditor();

  const { html, style, script } = state.values;

  // The value the frame watches: the three fields that reach the preview.
  const template = useMemo(() => ({ html, style, script }), [html, style, script]);

  const onMounted = useCallback(
    async ({ send, setLoading, position }) => {
      setLoading(true);

      // Cache-first: the site route above this screen has already read it,
      // and it is the shell and the values the sample page composes into.
      const siteResult = await client.query({
        query: SiteQL.get,
        variables: { siteId },
      });

      const { page, params } = samplePage(kind, await sampleOf(client, siteId, kind));
      const data = await fetchBlogData(client, siteId, page, params);
      data.templates = { ...data.templates, [kind]: template };

      setLoading(false);

      send("mount", {
        page,
        // A blog element renders through no section, and the sample page has
        // no other element — nothing to resolve.
        sections: {},
        site: _get(siteResult, SITE),
        params,
        data,
        selected: null,
        position,
      });
    },
    [client, siteId, kind, template]
  );

  return (
    <Preview.Frame
      // Absolute, and since P142 usually CROSS-ORIGIN: the frame is the same
      // preview route served from the sites hostname, so a tenant's `<script>`
      // does not run with this application's origin. `previewSrc` falls back to
      // this origin where there is no distribution name to build one from.
      src={previewSrc()}
      watch={template}
      selected={null}
      auto={auto}
      onAutoChange={setAuto}
      // One element and no way to select it, as in the section editor.
      highlight={false}
      hideHighlight
      onMounted={onMounted}
    />
  );
};
